import {
  ReactNode,
  useCallback,
  useState,
  createContext,
  useContext,
  ComponentPropsWithoutRef,
} from "react";
// constants
import { DEFAULT_SESSION_TYPE } from "constants/defaultSessionType";
// hooks
import useToggleModal from "hooks/useToggleModal";
// components
import SessionTypeEditor from "./components/session-type-editor/SessionTypeEditor";
import { AmplitudePageName } from "utils/amplitude";

const INITIAL_MODAL_PROPS = {
  onApply: () => null,
  getIsLabelInvalid: () => null,
};

// types
type TInitialSessionTypeEditorValue = Partial<SessionType>;

// constants
const SessionTypeEditorContext = createContext<{
  showSessionTypeEditor: boolean;
  openSessionTypeEditor: (
    props: ComponentPropsWithoutRef<typeof SessionTypeEditor>,
    amplitudePageName: AmplitudePageName,
    arg?: TInitialSessionTypeEditorValue,
  ) => void;
  closeSessionTypeEditor: () => void;
}>({
  showSessionTypeEditor: false,
  openSessionTypeEditor: () => null,
  closeSessionTypeEditor: () => null,
});

type Props = {
  children: ReactNode;
};

export const SessionTypeEditorContextProvider = ({ children }: Props) => {
  const [modalProps, setModalProps] =
    useState<ComponentPropsWithoutRef<typeof SessionTypeEditor>>(
      INITIAL_MODAL_PROPS,
    );

  const [pageName, setPageName] = useState<AmplitudePageName | null>(null);
  // session type editor
  const {
    show: showSessionTypeEditor,
    open: openSessionTypeEditor,
    close: closeSessionTypeEditor,
  } = useToggleModal(false);

  const [initialSessionType, setInitialSessionType] =
    useState<TInitialSessionTypeEditorValue>(DEFAULT_SESSION_TYPE);

  const handleOpenSessionTypeEditor = useCallback(
    (
      props: typeof modalProps,
      amplitudePageName,
      arg = DEFAULT_SESSION_TYPE,
    ) => {
      setInitialSessionType(arg);
      setModalProps(props);
      openSessionTypeEditor();
      setPageName(amplitudePageName);
    },
    [openSessionTypeEditor],
  );

  const handleCloseSessionTypeEditor = useCallback(() => {
    closeSessionTypeEditor();
    if (modalProps.onHide) {
      modalProps.onHide();
    }
    setInitialSessionType(DEFAULT_SESSION_TYPE);
    setModalProps(INITIAL_MODAL_PROPS);
    setPageName(null);
  }, [closeSessionTypeEditor, modalProps]);

  return (
    <SessionTypeEditorContext.Provider
      value={{
        showSessionTypeEditor,
        openSessionTypeEditor: handleOpenSessionTypeEditor,
        closeSessionTypeEditor: handleCloseSessionTypeEditor,
      }}
    >
      {children}
      <SessionTypeEditor
        {...modalProps}
        onApply={modalProps.onApply}
        getIsLabelInvalid={modalProps.getIsLabelInvalid}
        defaultValue={initialSessionType}
        show={showSessionTypeEditor}
        onHide={handleCloseSessionTypeEditor}
        amplitudePageName={pageName!}
      />
    </SessionTypeEditorContext.Provider>
  );
};
// hooks
export const useSessionTypeEditor = () => useContext(SessionTypeEditorContext);
